/**
 * Clay design system color palette
 *
 * Mirrors the Webflow Site variable collections → Swatches.
 * Use these tokens in modules instead of hard-coded hex values so design-token
 * changes propagate from a single source.
 *
 * Scale convention:
 *   50  = lightest tint
 *   500 = brand-saturated
 *   950 = darkest shade
 *
 * Not every family has every step — only the shades defined in the Webflow
 * variables panel are exported. Some values were transcribed from screenshots;
 * verify any that look off against the design tokens.
 *
 * @example
 *   import { colors, color } from "@/utils/colors";
 *
 *   element.style.background = colors.blueberry[500];
 *   element.style.color      = color("oat-200");
 */

export const colors = {
  black: "#000000",
  white: "#ffffff",

  grey: {
    50: "#f7f8f9",
    100: "#eff1f3",
    200: "#e6e8ec",
    300: "#d6d9df",
    400: "#bfc4cd",
    500: "#979da9",
    600: "#717989",
    700: "#525a69",
    800: "#3c414d",
    900: "#282c35",
    950: "#16181f",
  },

  ube: {
    50: "#f5f3ff",
    100: "#e9e4ff",
    200: "#d8d0ff",
    300: "#c1b0ff",
    400: "#a789fe",
    500: "#8b5cf6",
    600: "#7934f0",
    700: "#5c15cc",
    800: "#43089f",
    900: "#32037d",
  },

  blueberry: {
    50: "#ecf6ff",
    100: "#d7ebfe",
    200: "#bee0ff",
    300: "#83c4ff",
    400: "#4ca8fd",
    500: "#0382f7",
    600: "#0667d9",
    700: "#0053b5",
    800: "#01418d",
    900: "#002f67",
    950: "#001e4b",
  },

  matcha: {
    100: "#dbfee0",
    200: "#bcf4ca",
    300: "#84e7a5",
    400: "#3ece82",
    500: "#0dac65",
    600: "#078a52",
    700: "#02693e",
    800: "#02492a",
    900: "#03331d",
  },

  lemon: {
    100: "#fff8d2",
    200: "#fef0a4",
    300: "#fadf7c",
    400: "#f8cc65",
    500: "#fbbd41",
    600: "#fdad15",
    700: "#d08a11",
    800: "#9d6a09",
    900: "#7f500a",
    950: "#623c02",
  },

  tangerine: {
    50: "#fff3ed",
    100: "#ffe9d5",
    200: "#fdd4b7",
    300: "#ffbe90",
    400: "#ffa775",
    500: "#fc8936",
    600: "#fa6900",
    700: "#c34e1b",
    800: "#9f3e15",
    900: "#752b12",
    950: "#431407",
  },

  oat: {
    50: "#fffcfa",
    100: "#f9f8f6",
    200: "#f3f2ed",
    300: "#eee9df",
    400: "#dad4c8",
    500: "#c0bbaf",
    600: "#9f9b93",
    700: "#85817a",
    800: "#55534e",
    900: "#363430",
    950: "#1b1a18",
  },

  dragonfruit: {
    50: "#fff5fc",
    100: "#ffe5f7",
    200: "#ffd1f1",
    300: "#ffb8e8",
    400: "#ff9def",
    500: "#ff7ad5",
    600: "#ff52c2",
    700: "#f90ba6",
    800: "#cc0687",
    900: "#8b045c",
  },

  pomegranate: {
    100: "#ffebec",
    200: "#ffcdd3",
    300: "#fda1ab",
    400: "#fc7981",
    500: "#e94d68",
    600: "#dd2c53",
    700: "#b21a3f",
  },

  // From the Loam web library (5-shade scale; not in the main swatches set)
  lime: {
    100: "#fcfee2",
    200: "#eef673",
    300: "#cbd810",
    400: "#7f7f00",
    500: "#102b02",
  },

  slushie: {
    50: "#f5fdff",
    100: "#e0f8ff",
    200: "#c7f3ff",
    300: "#a3ebff",
    400: "#7ae3ff",
    500: "#3bd3fd",
    600: "#2abeea",
    700: "#02a8d4",
    800: "#0089ad",
    900: "#005870",
    950: "#00303d",
  },
} as const;

// -- types ----------------------------------------------------------------

export type Colors = typeof colors;
export type ColorFamily = keyof Colors;

type FamilyValue<F extends ColorFamily> = Colors[F];
type ShadeKey<F extends ColorFamily> = FamilyValue<F> extends string
  ? never
  : keyof FamilyValue<F>;

/**
 * Type-safe shade accessor — fully autocomplete-friendly.
 *
 * @example
 *   shade("blueberry", 500); // "#0382f7"
 */
export function shade<F extends ColorFamily>(
  family: F,
  step: ShadeKey<F>,
): string {
  const fam = colors[family] as unknown;
  if (typeof fam === "string") return fam;
  return (fam as Record<string | number, string>)[step as string | number];
}

/**
 * Loose string-key accessor for dynamic lookups (e.g. value from data attr).
 * Returns `"#000000"` if the name doesn't resolve.
 *
 * @example
 *   color("blueberry-500"); // "#0382f7"
 *   color("white");         // "#ffffff"
 */
export function color(name: string): string {
  const dash = name.indexOf("-");
  if (dash === -1) {
    const v = (colors as Record<string, unknown>)[name];
    return typeof v === "string" ? v : "#000000";
  }
  const family = name.slice(0, dash);
  const step = name.slice(dash + 1);
  const fam = (colors as Record<string, unknown>)[family];
  if (fam && typeof fam === "object") {
    const v = (fam as Record<string, string>)[step];
    if (typeof v === "string") return v;
  }
  return "#000000";
}
